import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchActiveChecklistsForOperator } from '@/modules/checklist/api';
import { useAuth } from '@/core/AuthContext';
import { useFactoryStore } from '@/core/hooks/useFactory';
import { useUserLocationIds } from '@/core/hooks/useUserLocations';
import { useAppScope } from '@/core/AppScopeContext';
import { useLanguage } from '@/core/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { 
  FileText, 
  AlertCircle, 
  Award, 
  Clock,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function OperatorDashboard() {
  const { user, profile } = useAuth();
  const { selectedFactoryId } = useFactoryStore();
  const { selectedTenantId } = useAppScope();
  const { areaIds, lineIds, stationIds, hasLocations } = useUserLocationIds();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Fetch pending startup work instructions for user's locations
  const { data: pendingInstructions = [] } = useQuery({
    queryKey: ['operator-pending-instructions', selectedTenantId, selectedFactoryId, areaIds, lineIds, stationIds],
    queryFn: async () => {
      if (!selectedFactoryId || !selectedTenantId || !hasLocations) return [];

      const checklists = await fetchActiveChecklistsForOperator(selectedTenantId, selectedFactoryId);

      return checklists
        .filter((checklist) => {
          if (checklist.station_id) return stationIds.includes(checklist.station_id);
          if (checklist.line_id) return lineIds.includes(checklist.line_id);
          if (checklist.area_id) return areaIds.includes(checklist.area_id);
          return true;
        })
        .slice(0, 10);
    },
    enabled: !!selectedTenantId && !!selectedFactoryId && hasLocations,
  });

  // Fetch user's competencies
  const { data: competencyStats } = useQuery({
    queryKey: ['operator-competency-stats', selectedFactoryId, user?.id],
    queryFn: async () => {
      if (!selectedFactoryId || !user) return { total: 0, active: 0, expiringSoon: 0 };

      const { data, error } = await supabase
        .schema('core')
        .from('user_competencies')
        .select('id, status, expires_at')
        .eq('user_id', user.id)
        .eq('factory_id', selectedFactoryId);

      if (error) throw error;

      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      const active = (data || []).filter(c => c.status === 'active').length;
      const expiringSoon = (data || []).filter(c => 
        c.status === 'active' && 
        c.expires_at && 
        new Date(c.expires_at) <= thirtyDaysFromNow
      ).length;

      return { total: data?.length || 0, active, expiringSoon };
    },
    enabled: !!selectedFactoryId && !!user,
  });

  if (!hasLocations) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t("operatorDashboard.title", "My Workspace")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {profile?.full_name || user?.email}
          </p>
        </div>

        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Location Assignments</h3>
            <p className="text-muted-foreground max-w-md">
              You haven't been assigned to any work areas yet. Please contact your supervisor to get assigned to areas, lines, or stations.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {t("operatorDashboard.title", "My Workspace")}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t("operatorDashboard.welcome", "Welcome back")}, {profile?.full_name || user?.email}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingInstructions.length}</p>
                <p className="text-xs text-muted-foreground">Work Instructions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <Award className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">{competencyStats?.active || 0}</p>
                <p className="text-xs text-muted-foreground">Active Skills</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-destructive/10">
                <Clock className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold">{competencyStats?.expiringSoon || 0}</p>
                <p className="text-xs text-muted-foreground">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Startup Work Instructions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              My Work Instructions
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/instructions')}>
              View all
            </Button>
          </CardHeader>
          <CardContent>
            {pendingInstructions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <FileText className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No work instructions assigned</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingInstructions.slice(0, 5).map((instruction) => (
                  <Button
                    key={instruction.id}
                    variant="ghost"
                    className="w-full justify-between h-auto py-3 px-3"
                    onClick={() => navigate('/instructions')}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium truncate">{instruction.name}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Competency Overview */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="h-4 w-4" />
              My Competencies
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/competency')}>
              View details
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-success" />
                <span className="text-sm">{competencyStats?.active || 0} Active</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-warning" />
                <span className="text-sm">{competencyStats?.expiringSoon || 0} Expiring Soon</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-muted-foreground" />
                <span className="text-sm">{competencyStats?.total || 0} Total</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
